import React, { useState, useEffect } from 'react';
import { 
  Zap, Calendar, Clock, DollarSign, Users, Send, Trash2, 
  CheckCircle, XCircle, AlertCircle, TrendingDown, Music 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

// Engineers list - synced with the booking form roster.
// Keep this list in sync with `/app/frontend/src/mock.js` engineers array.
const ENGINEERS = [
  { id: 1, name: "Trap Spade" },
  { id: 2, name: "Chapo" },
  { id: 5, name: "Duse" }
];

const DealsManagement = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sendingBlast, setSendingBlast] = useState(null);
  
  // New deal form state
  const [newDeal, setNewDeal] = useState({
    title: '',
    description: '',
    engineer_id: 1,
    engineer_name: 'Trap Spade',
    date: '',
    time: '',
    duration_hours: 3,
    original_price: 300,
    deal_price: 200,
    slots_available: 1
  });

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/deals`);
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeal = async () => {
    if (!newDeal.title || !newDeal.date || !newDeal.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/deals`, newDeal);
      toast.success('Deal created! Ready to send blast.');
      setShowCreateModal(false);
      setNewDeal({
        title: '',
        description: '',
        engineer_id: 1,
        engineer_name: 'Trap Spade',
        date: '',
        time: '',
        duration_hours: 3,
        original_price: 300,
        deal_price: 200,
        slots_available: 1
      });
      fetchDeals();
    } catch (error) {
      console.error('Error creating deal:', error);
      toast.error('Failed to create deal');
    }
  };

  const handleSendBlast = async (dealId) => {
    if (!window.confirm('Send this deal to ALL subscribers? This cannot be undone.')) {
      return;
    }

    try {
      setSendingBlast(dealId);
      const response = await axios.post(`${API}/deals/${dealId}/send-blast`);
      toast.success(response.data.message);
      fetchDeals();
    } catch (error) {
      console.error('Error sending blast:', error);
      toast.error('Failed to send blast');
    } finally {
      setSendingBlast(null);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Delete this deal?')) return;

    try {
      await axios.delete(`${API}/deals/${dealId}`);
      toast.success('Deal deleted');
      fetchDeals();
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast.error('Failed to delete deal');
    }
  };

  const handleEngineerChange = (engineerId) => {
    const engineer = ENGINEERS.find(e => e.id === parseInt(engineerId));
    setNewDeal(prev => ({
      ...prev,
      engineer_id: parseInt(engineerId),
      engineer_name: engineer?.name || ''
    }));
  };

  const getDealStatus = (deal) => {
    const now = new Date();
    const expires = new Date(deal.expires_at);
    const slotsRemaining = deal.slots_available - deal.slots_booked;
    
    if (slotsRemaining <= 0) {
      return { status: 'sold_out', label: 'SOLD OUT', color: 'green', icon: CheckCircle };
    }
    if (now > expires) {
      return { status: 'expired', label: 'EXPIRED', color: 'gray', icon: XCircle };
    }
    if (!deal.blast_sent) {
      return { status: 'draft', label: 'DRAFT', color: 'yellow', icon: AlertCircle };
    }
    return { status: 'active', label: 'ACTIVE', color: 'purple', icon: Zap };
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get today's date for min
  const today = new Date().toISOString().split('T')[0];

  // Time options
  const timeOptions = [];
  for (let h = 10; h <= 23; h++) {
    const hour = h > 12 ? h - 12 : h;
    const period = h >= 12 ? 'PM' : 'AM';
    timeOptions.push(`${hour}:00 ${period}`);
  }
  // Add late night options
  for (let h = 0; h <= 5; h++) {
    const hour = h === 0 ? 12 : h;
    timeOptions.push(`${hour}:00 AM`);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
            <Zap className="text-yellow-400" />
            Broadcast Deals
          </h2>
          <p className="text-gray-400 text-sm">
            Send last-minute deals to all subscribers
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2"
        >
          <Zap size={16} />
          Create New Deal
        </button>
      </div>

      {/* Deals List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Loading deals...</p>
        </div>
      ) : deals.length === 0 ? (
        <div className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 backdrop-blur-sm border border-yellow-500/20 rounded-xl p-12 text-center">
          <Zap className="text-yellow-400 mx-auto mb-4" size={48} />
          <p className="text-gray-400 mb-4">No deals created yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-semibold"
          >
            Create Your First Deal
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {deals.map((deal) => {
            const status = getDealStatus(deal);
            const StatusIcon = status.icon;
            const slotsRemaining = deal.slots_available - deal.slots_booked;
            const savings = deal.original_price - deal.deal_price;
            const savingsPercent = Math.round((savings / deal.original_price) * 100);
            
            return (
              <div
                key={deal.id}
                className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-xl p-6 hover:border-yellow-500/50 transition-all"
              >
                <div className="flex flex-col lg:flex-row justify-between gap-4">
                  {/* Deal Info */}
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{deal.title}</h3>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${status.color}-500/20 text-${status.color}-400 border border-${status.color}-500/30`}>
                            <StatusIcon size={12} className="mr-1" />
                            {status.label}
                          </span>
                        </div>
                        {deal.description && (
                          <p className="text-gray-400 text-sm mb-2">{deal.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 text-sm line-through">${deal.original_price}</p>
                        <p className="text-2xl font-bold text-green-400">${deal.deal_price}</p>
                        <p className="text-xs text-green-400">Save {savingsPercent}%</p>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-black/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-purple-400 text-xs mb-1">
                          <Music size={12} />
                          Engineer
                        </div>
                        <p className="text-white font-semibold">{deal.engineer_name}</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-blue-400 text-xs mb-1">
                          <Calendar size={12} />
                          Date
                        </div>
                        <p className="text-white font-semibold">{formatDate(deal.date)}</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-orange-400 text-xs mb-1">
                          <Clock size={12} />
                          Time
                        </div>
                        <p className="text-white font-semibold">{deal.time} ({deal.duration_hours}hr)</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-400 text-xs mb-1">
                          <Users size={12} />
                          Slots
                        </div>
                        <p className="text-white font-semibold">{deal.slots_booked}/{deal.slots_available} booked</p>
                      </div>
                    </div>

                    {/* Blast Stats */}
                    {deal.blast_sent && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-400">✓ Blast sent</span>
                          <span className="text-gray-400">{deal.blast_email_count || 0} emails</span>
                          <span className="text-gray-400">{deal.blast_sms_count || 0} SMS</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex lg:flex-col gap-2 justify-end">
                    {!deal.blast_sent && status.status !== 'expired' && (
                      <button
                        onClick={() => handleSendBlast(deal.id)}
                        disabled={sendingBlast === deal.id}
                        className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {sendingBlast === deal.id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            Send Blast
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteDeal(deal.id)}
                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Deal Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-yellow-900/30 border border-yellow-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Zap className="text-yellow-400" />
                Create New Deal
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                Set up a last-minute deal for your subscribers
              </p>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="text-sm text-yellow-400 font-semibold mb-2 block">Deal Title *</label>
                  <input
                    type="text"
                    value={newDeal.title}
                    onChange={(e) => setNewDeal(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Last Minute Studio Time!"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm text-yellow-400 font-semibold mb-2 block">Description (optional)</label>
                  <textarea
                    value={newDeal.description}
                    onChange={(e) => setNewDeal(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Cancellation opened up - grab this slot!"
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none resize-none"
                  />
                </div>

                {/* Engineer */}
                <div>
                  <label className="text-sm text-yellow-400 font-semibold mb-2 block">Engineer *</label>
                  <select
                    value={newDeal.engineer_id}
                    onChange={(e) => handleEngineerChange(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                  >
                    {ENGINEERS.map(eng => (
                      <option key={eng.id} value={eng.id}>{eng.name}</option>
                    ))}
                  </select>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-yellow-400 font-semibold mb-2 block">Date *</label>
                    <input
                      type="date"
                      min={today}
                      value={newDeal.date}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-yellow-400 font-semibold mb-2 block">Time *</label>
                    <select
                      value={newDeal.time}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                    >
                      <option value="">Select time</option>
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Duration & Slots */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-yellow-400 font-semibold mb-2 block">Duration (hours)</label>
                    <select
                      value={newDeal.duration_hours}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, duration_hours: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 8, 10, 12].map(h => (
                        <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-yellow-400 font-semibold mb-2 block">Available Slots</label>
                    <select
                      value={newDeal.slots_available}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, slots_available: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                    >
                      {[1, 2, 3, 4, 5].map(s => (
                        <option key={s} value={s}>{s} slot{s > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-400 font-semibold mb-2 block">Original Price ($)</label>
                    <input
                      type="number"
                      value={newDeal.original_price}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, original_price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-green-400 font-semibold mb-2 block">Deal Price ($) *</label>
                    <input
                      type="number"
                      value={newDeal.deal_price}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, deal_price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                  </div>
                </div>

                {/* Savings Preview */}
                {newDeal.original_price > 0 && newDeal.deal_price > 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-green-400">
                      <TrendingDown size={20} />
                      <span className="text-lg font-bold">
                        {Math.round(((newDeal.original_price - newDeal.deal_price) / newDeal.original_price) * 100)}% OFF
                      </span>
                      <span className="text-gray-400">
                        (Save ${(newDeal.original_price - newDeal.deal_price).toFixed(0)})
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDeal}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <Zap size={16} />
                  Create Deal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealsManagement;
